// ./src/components/features/Events/CopyEventDrawer.tsx
//
// ROADMAP #116 — COPY AN EVENT (the API's docs/COPY-EVENT.md, agreed with the owner 2026-10-25). Asks for the copy's
// name and start, then offers a checklist built from what THIS event holds ("Tickets — 7 ticket types"): setup ticked,
// people (partners, vendors, the guest list) unticked, the crew ticked. The server makes a DRAFT and decides everything
// else — what moves, what is left out, what the plan allows; this drawer only asks and shows what it answers.
// A URL stage of the dashboard (`/events/:id/copy`) and of All Events (`/events/all/copy/:id`).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Alert, Button, Checkbox, Group, List, Loader, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { utcToZonedString, zonedStringToUtc } from '@em/utils';

import { AppDrawer, DiscardGuard } from '@/components/generic/AppDrawer';
import { ErrorAlert } from '@/components/generic/ErrorAlert';
import { AppIcon, DateTimeField, TextField } from '@/components/generic';
import { EventService, Event, EventCopyGroup, EventCopyPlan, EventCopyReport } from '@/utils/api';
import { getFormErrors, hasAnyFieldError, recordHasRight } from '@/utils';
import { useFormSnapshot } from '@/hooks';
import type { OpResult } from '@sdflc/api-helpers';

import { eventZoneNote } from './eventZone';

dayjs.extend(utc);

/**
 * May this person copy this event? The SAME rule the server enforces on `eventCopy` (`loadOwnedEvents`): only the event's
 * own account — a crew member never copies an event (into their account or anyone's). The API marks it on every event it
 * returns (`rights` holds `duplicate` only for the account's own). ⚠ ONE function for the menu entry AND the URL stage.
 */
export const canCopyEvent = (event: Pick<Event, 'rights'> | null | undefined, crew: { loading: boolean; isCrewMode: boolean }): boolean =>
  !!event && !crew.loading && !crew.isCrewMode && recordHasRight(event, 'duplicate');

export interface CopyEventDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  /** The copy was made — the page opens it. */
  onCopied: (newEventId: string) => void;
}

/**
 * The same time of day a week later — what a copy most often is (a weekly class; next month's market is a quick change).
 * ⚠ ON THE EVENT'S CLOCK: the picker's wall text is stepped by calendar days. Adding 7 × 24 hours to the instant lands an
 * hour off across a clock change (a day is not 24 hours).
 */
const aWeekLater = (wall: string | null): string | null =>
  wall ? dayjs.utc(`${wall.slice(0, 10)}T${wall.slice(11, 19) || '00:00:00'}Z`).add(7, 'day').format('YYYY-MM-DD HH:mm:ss') : null;

const groupsKey = (keys: string[]): string => [...keys].sort().join(',');

export const CopyEventDrawer: React.FC<CopyEventDrawerProps> = ({ opened, onClose, eventId, onCopied }) => {
  const { t } = useTranslation(['events', 'common']);
  const [plan, setPlan] = useState<EventCopyPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);
  const [title, setTitle] = useState('');
  // A wall clock in the EVENT's zone (what DateTimeField shows); sent as an instant.
  const [startsAt, setStartsAt] = useState<string | null>(null);
  const [ticked, setTicked] = useState<string[]>([]);
  const [copying, setCopying] = useState(false);
  const [saveResult, setSaveResult] = useState<OpResult | null>(null);
  const [startMissing, setStartMissing] = useState(false);
  const [report, setReport] = useState<EventCopyReport | null>(null);

  const { isDirty: formChanged, seedFrom, clear } = useFormSnapshot({ title, startsAt, ticked: groupsKey(ticked) });

  // ⚠ Read fresh on every open: the checklist counts what the event holds NOW.
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    const result = await EventService.copyPlan(eventId);

    setLoading(false);

    if (!result.didSucceed()) {
      setPlan(null);
      setLoadError(result);

      return;
    }

    const next = (result.getDataFirst() as EventCopyPlan) ?? null;
    const tz = next?.timezone ?? null;
    const seededTitle = next?.title ?? '';
    const seededStart = aWeekLater(utcToZonedString(next?.startsAt ?? null, tz, false));
    const seededTicked = (next?.groups ?? []).filter((g) => g.defaultOn).map((g) => g.key);

    setPlan(next);
    setTitle(seededTitle);
    setStartsAt(seededStart);
    setTicked(seededTicked);
    seedFrom({ title: seededTitle, startsAt: seededStart, ticked: groupsKey(seededTicked) });
  }, [eventId, seedFrom]);

  useEffect(() => {
    if (!opened || !eventId) {
      clear();

      return;
    }

    setPlan(null);
    setSaveResult(null);
    setStartMissing(false);
    setReport(null);
    void load();
  }, [opened, eventId, load, clear]);

  const toggle = useCallback((key: string, on: boolean) => {
    setTicked((prev) => (on ? [...new Set([...prev, key])] : prev.filter((k) => k !== key)));
  }, []);

  const copy = async () => {
    // Presence only — the server owns every other rule.
    if (!startsAt) {
      setStartMissing(true);

      return;
    }

    setStartMissing(false);
    setCopying(true);
    setSaveResult(null);

    const result = await EventService.copy({
      eventId,
      title: title.trim() || null,
      startsAt: zonedStringToUtc(startsAt, plan?.timezone ?? null) ?? startsAt,
      groups: ticked,
    });

    setCopying(false);

    if (!result.didSucceed()) {
      setSaveResult(result);

      return;
    }

    const done = (result.getDataFirst() as EventCopyReport) ?? null;

    if (!done?.event?.id) {
      setSaveResult(result);

      return;
    }

    // Opening the copy IS the confirmation; when something was left out the host reads why first.
    if ((done.leftOut ?? []).length === 0) {
      onCopied(String(done.event.id));

      return;
    }

    setReport(done);
  };

  /**
   * ⚠ ONLY the inputs this drawer renders (the KNOWN_FORM_FIELDS rule): `title` and `startsAt` under their fields,
   * `groups` under the checklist. `eventId` has no input — its refusals belong in the footer.
   */
  const { fields: fieldErrors, genericErrors } = getFormErrors(saveResult, ['title', 'startsAt', 'groups'] as const);
  const setup = useMemo(() => (plan?.groups ?? []).filter((g) => !g.people), [plan]);
  const people = useMemo(() => (plan?.groups ?? []).filter((g) => g.people), [plan]);
  const zoneNote = eventZoneNote(t, plan?.timezone ?? null);
  const isDirty = !report && formChanged;

  const renderGroup = (g: EventCopyGroup) => (
    <Checkbox
      key={g.key}
      size="md"
      checked={ticked.includes(g.key)}
      onChange={(e) => toggle(g.key, e.currentTarget.checked)}
      disabled={copying}
      label={g.label}
      description={
        <Stack gap={2} component="span">
          <Text size="md" c="dimmed" component="span">
            {g.detail}
          </Text>
          {g.planNote && (
            <Text size="md" c="orange.8" component="span">
              {t('events:copy.planNote', { defaultValue: 'Left out: {{note}}', note: g.planNote })}
            </Text>
          )}
        </Stack>
      }
    />
  );

  const footer = report ? (
    <Group justify="flex-end" w="100%">
      <Button size="md" onClick={() => onCopied(String(report.event.id))}>
        {t('events:copy.open', { defaultValue: 'Open the copy' })}
      </Button>
    </Group>
  ) : (
    <Group justify="flex-end" w="100%" gap="sm">
      <DiscardGuard>
        {(leave) => (
          <Button variant="default" size="md" onClick={() => leave()} disabled={copying}>
            {t('common:buttons.cancel', { defaultValue: 'Cancel' })}
          </Button>
        )}
      </DiscardGuard>
      <Button size="md" onClick={copy} loading={copying} disabled={loading || !plan} leftSection={<AppIcon iconName="Copy" size={16} />}>
        {t('events:copy.confirm', { defaultValue: 'Copy event' })}
      </Button>
    </Group>
  );

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t('events:copy.title', { defaultValue: 'Copy this event' })}
      position="right"
      size="lg"
      actionType="custom"
      customActions={footer}
      isDirty={isDirty}
      errors={genericErrors.length > 0 ? genericErrors : undefined}
      hasFieldErrors={hasAnyFieldError(fieldErrors) || startMissing}
      closable={!copying}
      loading={copying}
    >
      {report ? (
        <Stack gap="sm">
          <Text size="md">
            {t('events:copy.made', { defaultValue: '“{{title}}” is ready as a draft. Some of what you ticked was left out:', title: report.event.title ?? '' })}
          </Text>
          <List size="md" spacing={4}>
            {report.leftOut.map((l) => (
              <List.Item key={`${l.group}:${l.label}`}>
                <Text size="md" component="span" fw={600}>
                  {l.label}
                </Text>
                <Text size="md" component="span">
                  {' — '}
                  {l.reason}
                </Text>
              </List.Item>
            ))}
          </List>
        </Stack>
      ) : loading ? (
        <Group gap="xs">
          <Loader size="sm" />
          <Text size="md" c="dimmed">
            {t('events:copy.loading', { defaultValue: 'Looking at what this event holds…' })}
          </Text>
        </Group>
      ) : loadError ? (
        <ErrorAlert error={loadError} title="We could not read this event" page="Copy event" operation="eventCopyPlan" />
      ) : plan ? (
        <Stack gap="md">
          <Text size="md" c="dimmed">
            {t('events:copy.intro', {
              defaultValue:
                'Makes a new draft. Nothing is sent to anyone, and nothing a guest did or was given comes with it — RSVPs, orders, check-ins, answers and sign-ups stay with this event.',
            })}
          </Text>
          <TextField
            name="title"
            data-autofocus
            label={t('events:copy.name', { defaultValue: 'Name of the copy' })}
            value={title}
            onChange={(_name, value) => setTitle(value)}
            maxLength={256}
            error={fieldErrors.title}
          />
          <DateTimeField
            name="startsAt"
            label={
              plan.recurring
                ? t('events:copy.seriesStart', { defaultValue: 'The series starts' })
                : t('events:copy.starts', { defaultValue: 'Starts' })
            }
            description={
              [
                plan.recurring
                  ? t('events:copy.seriesHint', { defaultValue: 'Its dates are made again from the same repeat pattern.' })
                  : t('events:copy.startHint', { defaultValue: 'Everything dated moves with it — sessions, sales windows, tasks.' }),
                zoneNote,
              ]
                .filter(Boolean)
                .join(' ')
            }
            value={startsAt}
            onChange={(_name, value) => {
              setStartsAt(value);
              setStartMissing(false);
            }}
            timeZone={plan.timezone ?? null}
            leftSection={<AppIcon iconName="Calendar" size={16} />}
            required
            error={fieldErrors.startsAt ?? (startMissing ? t('events:copy.startMissing', { defaultValue: 'Pick when the copy starts' }) : undefined)}
          />

          {setup.length > 0 && (
            <Stack gap="sm">
              <Title order={4}>{t('events:copy.setup', { defaultValue: 'What to copy' })}</Title>
              {setup.map(renderGroup)}
            </Stack>
          )}
          {people.length > 0 && (
            <Stack gap="sm">
              <Title order={4}>{t('events:copy.people', { defaultValue: 'People' })}</Title>
              {people.map(renderGroup)}
            </Stack>
          )}
          {fieldErrors.groups && (
            <Text size="md" c="red">
              {fieldErrors.groups}
            </Text>
          )}

          <Alert color="blue" variant="light" icon={<AppIcon iconName="Photo" size={18} />}>
            <Text size="md">{plan.filesNote}</Text>
          </Alert>
        </Stack>
      ) : null}
    </AppDrawer>
  );
};
